import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Anchor, 
  LayoutDashboard, 
  Ship, 
  Calendar,
  Users,
  Euro,
  LogOut,
  Menu,
  X,
  Plus,
  Pencil,
  Trash2,
  Eye,
  Download,
  Search,
  Filter,
  ChevronDown,
  CalendarRange,
  BarChart3,
  Save,
  Mail,
  Upload,
  Printer,
  FileText,
  Image as ImageIcon,
  Star,
  MessageSquare,
  Reply,
  UserPlus,
  Check,
  Edit3,
  ClipboardCheck,
  ScrollText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const formatDateFr = (dateStr) => {
  if (!dateStr) return '-';
  try { return format(parseISO(dateStr), 'dd/MM/yyyy'); } catch { return dateStr; }
};
import AgendaView from '@/components/AgendaView';
import ConditionReportTab from '@/components/ConditionReportTab';
import CGVEditor from '@/components/CGVEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logos
const LOGO_HEADER = "https://customer-assets.emergentagent.com/job_yacht-reserve-pro/artifacts/t54ah8ex_logo%203%20BELLA%20BARCA.png";
const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_yacht-reserve-pro/artifacts/k3tg8tl5_2.png";

const BOAT_NAMES = { 'roto-450-s-1': 'MARINU', 'roto-450-s-2': 'DAMIANU', 'roto-450-s-3': 'AMBROGGIU' };

const navItems = [
  { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { id: 'agenda', label: 'Agenda', icon: CalendarRange },
  { id: 'bookings', label: 'Réservations', icon: Calendar },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'boats', label: 'Bateaux', icon: Ship },
  { id: 'condition', label: 'État des lieux', icon: ClipboardCheck },
  { id: 'cgv', label: 'CGV', icon: ScrollText },
  { id: 'pricing', label: 'Tarifs', icon: Euro },
  { id: 'customers', label: 'Clients', icon: Users },
  { id: 'analytics', label: 'Analytique', icon: BarChart3 }
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [boats, setBoats] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [messages, setMessages] = useState([]);
  
  // Filter states
  const [bookingFilter, setBookingFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchBookings(),
        fetchBoats(),
        fetchPricing(),
        fetchCustomers(),
        fetchMessages()
      ]);
    } catch (e) {
      if (e.message.includes('401')) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const res = await fetch(`${API}/admin/dashboard`, { headers: authHeaders });
    if (!res.ok) throw new Error(res.status);
    setStats(await res.json());
  };

  const fetchBookings = async () => {
    const res = await fetch(`${API}/admin/bookings`, { headers: authHeaders });
    if (!res.ok) throw new Error(res.status);
    setBookings(await res.json());
  };

  const fetchBoats = async () => {
    const res = await fetch(`${API}/admin/boats`, { headers: authHeaders });
    if (!res.ok) throw new Error(res.status);
    setBoats(await res.json());
  };

  const fetchPricing = async () => {
    const res = await fetch(`${API}/admin/pricing`, { headers: authHeaders });
    if (!res.ok) throw new Error(res.status);
    setPricing(await res.json());
  };

  const fetchCustomers = async () => {
    const res = await fetch(`${API}/admin/customers`, { headers: authHeaders });
    if (!res.ok) throw new Error(res.status);
    setCustomers(await res.json());
  };

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API}/admin/messages`, { headers: authHeaders });
      if (res.ok) setMessages(await res.json());
    } catch {}
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const res = await fetch(`${API}/admin/bookings/${bookingId}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success('Statut mis à jour');
      fetchBookings();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Supprimer cette réservation ?')) return;
    try {
      const res = await fetch(`${API}/admin/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Réservation supprimée');
      fetchBookings();
      fetchStats();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const downloadPDF = async (bookingId, type) => {
    try {
      const endpoint = type === 'contract' 
        ? `${API}/bookings/${bookingId}/contract/pdf`
        : `${API}/bookings/${bookingId}/invoice/pdf`;
      
      const res = await fetch(endpoint);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_${bookingId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const filteredBookings = bookings.filter(b => {
    if (bookingFilter !== 'all' && b.status !== bookingFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        b.customer?.first_name?.toLowerCase().includes(search) ||
        b.customer?.last_name?.toLowerCase().includes(search) ||
        b.customer?.email?.toLowerCase().includes(search) ||
        b.id.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const getStatusBadge = (status) => {
    const classes = {
      pending: 'status-badge status-pending',
      confirmed: 'status-badge status-confirmed',
      paid: 'status-badge status-paid',
      cancelled: 'status-badge status-cancelled',
      completed: 'status-badge status-paid'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      paid: 'Payée',
      cancelled: 'Annulée',
      completed: 'Terminée'
    };
    return <span className={classes[status] || 'status-badge'}>{labels[status] || status}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-[280px] min-h-screen bg-[#0a57a4] text-white transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 transition-transform duration-300`}>
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="block">
            <img src={LOGO_WHITE} alt="A Bella Barca" className="h-16" />
          </Link>
        </div>

        <nav className="mt-6">
          {navItems.map(item => {
            const unread = item.id === 'messages' ? messages.filter(m => !m.is_read).length : 0;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full text-left flex items-center gap-3 px-6 py-3 transition-colors ${activeTab === item.id ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {unread > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{unread}</span>}
              </button>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <button 
            onClick={logout}
            className="w-full text-left flex items-center gap-3 px-6 py-3 text-red-300 hover:text-red-200 hover:bg-white/5 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen overflow-x-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-[#dec5ae]/30 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <button 
              className="lg:hidden p-2 text-[#0a57a4]"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="font-heading text-xl text-[#0a57a4]">
              {navItems.find(i => i.id === activeTab)?.label}
            </h1>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-[#858275] hover:text-[#0a57a4]">
                Voir le site →
              </Link>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && stats && (
            <div className="space-y-8">
              {/* Stats cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Réservations', value: stats.total_bookings, icon: Calendar },
                  { label: 'En attente', value: stats.pending_bookings, icon: Calendar },
                  { label: 'Payées', value: stats.paid_bookings, icon: Euro },
                  { label: 'Chiffre d\'affaires', value: `${(stats.total_revenue / 100).toFixed(0)}€`, icon: Euro }
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white p-6 rounded-sm border border-[#dec5ae]/30"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#858275] truncate mr-2">{stat.label}</span>
                      <stat.icon className="w-5 h-5 text-[#099dbb] flex-shrink-0" />
                    </div>
                    <p className="font-heading text-2xl lg:text-3xl text-[#0a57a4] truncate">{stat.value}</p>
                  </motion.div>
                ))}
              </div>

              {/* Recent bookings */}
              <div className="bg-white rounded-sm border border-[#dec5ae]/30">
                <div className="p-4 border-b border-[#dec5ae]/30">
                  <h3 className="font-heading text-lg text-[#0a57a4]">Dernières réservations</h3>
                </div>
                {/* Desktop table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Référence</th>
                        <th>Client</th>
                        <th>Date</th>
                        <th>Montant</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.slice(0, 5).map(booking => (
                        <tr key={booking.id}>
                          <td className="font-mono text-sm">{booking.id.slice(0, 8).toUpperCase()}</td>
                          <td>{booking.customer?.first_name} {booking.customer?.last_name}</td>
                          <td>{formatDateFr(booking.date)}</td>
                          <td>{(booking.total_price / 100).toFixed(0)}€</td>
                          <td>{getStatusBadge(booking.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="lg:hidden divide-y divide-[#dec5ae]/20">
                  {bookings.slice(0, 5).map(booking => (
                    <div key={booking.id} className="p-3 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-[#0a57a4]">{booking.customer?.first_name} {booking.customer?.last_name}</span>
                        {getStatusBadge(booking.status)}
                      </div>
                      <div className="flex items-center justify-between text-xs text-[#858275]">
                        <span>{formatDateFr(booking.date)}</span>
                        <span className="font-semibold text-[#0a57a4]">{(booking.total_price / 100).toFixed(0)}€</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Agenda Tab */}
          {activeTab === 'agenda' && (
            <AgendaView token={token} />
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <BookingsTab
              bookings={bookings}
              filteredBookings={filteredBookings}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              bookingFilter={bookingFilter}
              setBookingFilter={setBookingFilter}
              getStatusBadge={getStatusBadge}
              updateBookingStatus={updateBookingStatus}
              downloadPDF={downloadPDF}
              deleteBooking={deleteBooking}
              token={token}
            />
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <MessagesTab messages={messages} token={token} onRefresh={fetchData} customers={customers} />
          )}

          {/* Boats Tab */}
          {activeTab === 'boats' && (
            <BoatsTab boats={boats} token={token} onRefresh={fetchData} />
          )}

          {/* Condition Report Tab */}
          {activeTab === 'condition' && (
            <ConditionReportTab boats={boats} token={token} />
          )}

          {/* CGV Tab */}
          {activeTab === 'cgv' && (
            <CGVEditor token={token} />
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <PricingTab pricing={pricing} token={token} onRefresh={fetchData} />
          )}

          {/* Customers Tab */}
          {activeTab === 'customers' && (
            <CustomersTab customers={customers} token={token} onRefresh={fetchData} />
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <AnalyticsView token={token} />
          )}
        </div>
      </main>
    </div>
  );
}

function AnalyticsView({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed');
        setData(await res.json());
      } catch {
        // handle silently
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [token]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#099dbb]/30 border-t-[#099dbb] rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-[#858275] text-center py-12">Aucune donnée disponible</p>;

  const sortedMonths = Object.keys(data.monthly_revenue || {}).sort();
  const maxRevenue = Math.max(...Object.values(data.monthly_revenue || { 0: 1 }));
  const sortedSlots = Object.entries(data.slot_popularity || {}).sort((a, b) => b[1] - a[1]);
  const maxSlotCount = sortedSlots.length > 0 ? sortedSlots[0][1] : 1;

  return (
    <div className="space-y-6" data-testid="analytics-view">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Chiffre d\'affaires TTC', value: `${(data.total_revenue / 100).toFixed(0)}€`, sub: `HT: ${(data.total_revenue / 100 / 1.20).toFixed(0)}€ | TVA: ${(data.total_revenue / 100 - data.total_revenue / 100 / 1.20).toFixed(0)}€` },
          { label: 'Réservations totales', value: data.total_bookings, sub: `Panier moyen: ${(data.avg_booking_value / 100).toFixed(0)}€` },
          { label: 'Mois les plus actifs', value: sortedMonths.length, sub: `${sortedMonths.length} mois d'activité` }
        ].map((kpi, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
            className="bg-white p-6 rounded-sm border border-[#dec5ae]/30">
            <p className="text-sm text-[#858275]">{kpi.label}</p>
            <p className="font-heading text-3xl text-[#0a57a4] my-1">{kpi.value}</p>
            <p className="text-xs text-[#858275]">{kpi.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* Monthly Revenue Chart */}
      <div className="bg-white p-6 rounded-sm border border-[#dec5ae]/30">
        <h3 className="font-heading text-lg text-[#0a57a4] mb-4">Revenus mensuels (TTC)</h3>
        <div className="space-y-2">
          {sortedMonths.map((month, i) => {
            const rev = data.monthly_revenue[month];
            const bookCount = data.monthly_bookings[month] || 0;
            const pct = (rev / maxRevenue) * 100;
            return (
              <motion.div key={month} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3">
                <span className="text-xs text-[#858275] w-20 text-right">{month}</span>
                <div className="flex-1 h-8 bg-[#FDFBF7] rounded-sm overflow-hidden relative">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ delay: i * 0.05 + 0.2, duration: 0.5 }}
                    className="h-full rounded-sm"
                    style={{ background: `linear-gradient(90deg, #0a57a4, #099dbb)` }}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-[#0a57a4]">
                    {(rev / 100).toFixed(0)}€ ({bookCount} rés.)
                  </span>
                </div>
              </motion.div>
            );
          })}
          {sortedMonths.length === 0 && <p className="text-center text-sm text-[#858275] py-8">Aucune donnée de revenus</p>}
        </div>
      </div>

      {/* Slot Popularity */}
      <div className="bg-white p-6 rounded-sm border border-[#dec5ae]/30">
        <h3 className="font-heading text-lg text-[#0a57a4] mb-4">Créneaux les plus populaires</h3>
        <div className="space-y-2">
          {sortedSlots.slice(0, 10).map(([slot, count], i) => {
            const pct = (count / maxSlotCount) * 100;
            return (
              <motion.div key={slot} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3">
                <span className="text-xs text-[#858275] w-32 text-right truncate" title={slot}>{slot}</span>
                <div className="flex-1 h-7 bg-[#FDFBF7] rounded-sm overflow-hidden relative">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ delay: i * 0.05 + 0.2, duration: 0.5 }}
                    className="h-full rounded-sm bg-[#099dbb]"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-[#0a57a4]">
                    {count} réservation{count > 1 ? 's' : ''}
                  </span>
                </div>
              </motion.div>
            );
          })}
          {sortedSlots.length === 0 && <p className="text-center text-sm text-[#858275] py-8">Aucune donnée de créneaux</p>}
        </div>
      </div>
    </div>
  );
}


function BookingsTab({ bookings, filteredBookings, searchTerm, setSearchTerm, bookingFilter, setBookingFilter, getStatusBadge, updateBookingStatus, downloadPDF, deleteBooking, token }) {
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [exporting, setExporting] = useState(false);
  const [sendingReview, setSendingReview] = useState(null);
  const [sendingBulkReview, setSendingBulkReview] = useState(false);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const startEditNotes = (booking) => {
    setEditingNotes(booking.id);
    setNotesText(booking.notes || '');
  };

  const saveNotes = async (bookingId) => {
    setSavingNotes(true);
    try {
      const res = await fetch(`${API}/admin/bookings/${bookingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ notes: notesText })
      });
      if (!res.ok) throw new Error();
      toast.success('Notes enregistrées');
      // Update local booking data
      const b = filteredBookings.find(b => b.id === bookingId);
      if (b) b.notes = notesText;
      setEditingNotes(null);
    } catch { toast.error('Erreur sauvegarde notes'); }
    finally { setSavingNotes(false); }
  };

  const sendReviewRequest = async (bookingId) => {
    setSendingReview(bookingId);
    try {
      const res = await fetch(`${API}/admin/bookings/${bookingId}/send-review-request`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      toast.success('Demande d\'avis envoyée !');
    } catch { toast.error('Erreur envoi avis'); }
    finally { setSendingReview(null); }
  };

  const sendBulkReviewRequests = async () => {
    setSendingBulkReview(true);
    try {
      const res = await fetch(`${API}/admin/bookings/send-review-bulk`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      toast.success(data.message);
    } catch { toast.error('Erreur envoi groupé'); }
    finally { setSendingBulkReview(false); }
  };

  const exportBookings = async () => {
    setExporting(true);
    try {
      let url = `${API}/admin/bookings/export?`;
      if (exportStart) url += `start_date=${exportStart}&`;
      if (exportEnd) url += `end_date=${exportEnd}&`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `factures_${exportStart || 'all'}_${exportEnd || 'all'}.csv`;
      a.click();
      toast.success('Export téléchargé');
    } catch { toast.error('Erreur export'); }
    finally { setExporting(false); }
  };

  const printBookings = () => {
    const printContent = document.getElementById('bookings-print-area');
    if (!printContent) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Factures - A Bella Barca</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;font-size:12px}
      table{width:100%;border-collapse:collapse;margin-top:10px}
      th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
      th{background:#0a57a4;color:white}
      .header{text-align:center;margin-bottom:20px}
      .total{margin-top:15px;text-align:right;font-weight:bold}
      </style></head><body>`);
    win.document.write(`<div class="header"><h2>A BELLA BARCA</h2><p>Port de Sant'Ambroggio, 20260 Lumio, Corse</p><p>Factures ${exportStart ? 'du ' + exportStart : ''} ${exportEnd ? 'au ' + exportEnd : ''}</p></div>`);
    win.document.write('<table><tr><th>Réf</th><th>Date</th><th>Client</th><th>Créneau</th><th>TTC</th><th>HT</th><th>TVA 20%</th><th>Statut</th></tr>');
    let totalTTC = 0, totalHT = 0, totalTVA = 0;
    const filtered = filteredBookings.filter(b => {
      if (exportStart && b.date < exportStart) return false;
      if (exportEnd && b.date > exportEnd) return false;
      return true;
    });
    filtered.forEach(b => {
      const ttc = b.total_price / 100;
      const ht = Math.round(ttc / 1.20 * 100) / 100;
      const tva = Math.round((ttc - ht) * 100) / 100;
      totalTTC += ttc; totalHT += ht; totalTVA += tva;
      win.document.write(`<tr><td>${b.id.slice(0,8).toUpperCase()}</td><td>${formatDateFr(b.date)}</td><td>${b.customer?.first_name} ${b.customer?.last_name}</td><td>${b.time_slot}</td><td>${ttc.toFixed(2)}€</td><td>${ht.toFixed(2)}€</td><td>${tva.toFixed(2)}€</td><td>${b.status}</td></tr>`);
    });
    win.document.write('</table>');
    win.document.write(`<div class="total">Total TTC: ${totalTTC.toFixed(2)}€ | HT: ${totalHT.toFixed(2)}€ | TVA: ${totalTVA.toFixed(2)}€ | ${filtered.length} facture(s)</div>`);
    win.document.write('</body></html>');
    win.document.close();
    win.print();
  };

  return (
    <div className="space-y-4">
      {/* Export toolbar */}
      <div className="bg-white p-4 rounded-sm border border-[#dec5ae]/30 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-[#858275]">
          <FileText className="w-4 h-4" /> Export comptable :
        </div>
        <Input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)} className="w-40 text-sm" placeholder="Du" data-testid="export-start" />
        <Input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)} className="w-40 text-sm" placeholder="Au" data-testid="export-end" />
        <Button size="sm" variant="outline" onClick={exportBookings} disabled={exporting} data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-1" /> {exporting ? 'Export...' : 'CSV'}
        </Button>
        <Button size="sm" variant="outline" onClick={printBookings} data-testid="print-bookings-btn">
          <Printer className="w-4 h-4 mr-1" /> Imprimer
        </Button>
        <Button size="sm" variant="outline" onClick={sendBulkReviewRequests} disabled={sendingBulkReview} className="text-yellow-700 border-yellow-300 hover:bg-yellow-50" data-testid="bulk-review-btn">
          <Star className="w-4 h-4 mr-1" /> {sendingBulkReview ? 'Envoi...' : 'Demandes d\'avis'}
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
          <Input placeholder="Rechercher par nom, email, référence..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="booking-search" />
        </div>
        <Select value={bookingFilter} onValueChange={setBookingFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="booking-filter">
            <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Filtrer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="confirmed">Confirmées</SelectItem>
            <SelectItem value="paid">Payées</SelectItem>
            <SelectItem value="cancelled">Annulées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div id="bookings-print-area" className="bg-white rounded-sm border border-[#dec5ae]/30 overflow-hidden">
        {/* Desktop table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Réf</th><th>Client</th><th>Date</th><th>Créneau</th>
                <th>TTC</th><th>HT</th><th>TVA</th><th>Statut</th><th>Notes</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map(b => {
                const ttc = (b.total_price / 100);
                const ht = (ttc / 1.20).toFixed(2);
                const tva = (ttc - parseFloat(ht)).toFixed(2);
                return (
                  <tr key={b.id}>
                    <td className="font-mono text-xs">{b.id.slice(0, 8).toUpperCase()}</td>
                    <td><p className="font-medium text-sm">{b.customer?.first_name} {b.customer?.last_name}</p><p className="text-[10px] text-[#858275]">{b.customer?.email}</p></td>
                    <td className="text-sm">{formatDateFr(b.date)}</td>
                    <td className="text-sm">{b.time_slot}</td>
                    <td className="font-semibold text-sm">{ttc.toFixed(0)}€</td>
                    <td className="text-xs text-[#858275]">{ht}€</td>
                    <td className="text-xs text-[#858275]">{tva}€</td>
                    <td>{getStatusBadge(b.status)}</td>
                    <td>
                      {editingNotes === b.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            value={notesText}
                            onChange={e => setNotesText(e.target.value)}
                            className="border border-[#dec5ae]/50 rounded px-2 py-1 text-xs w-40"
                            placeholder="Notes internes..."
                            autoFocus
                            onKeyDown={e => e.key === 'Enter' && saveNotes(b.id)}
                            data-testid={`notes-input-${b.id.slice(0,8)}`}
                          />
                          <Button variant="ghost" size="sm" onClick={() => saveNotes(b.id)} disabled={savingNotes} className="text-green-600 h-6 w-6 p-0"><Check className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditingNotes(null)} className="text-red-500 h-6 w-6 p-0"><X className="w-3.5 h-3.5" /></Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 cursor-pointer group" onClick={() => startEditNotes(b)} data-testid={`notes-cell-${b.id.slice(0,8)}`}>
                          {b.notes ? (
                            <span className="text-xs text-[#858275] max-w-[150px] truncate" title={b.notes}>{b.notes}</span>
                          ) : (
                            <span className="text-xs text-[#858275]/40 group-hover:text-[#099dbb]">+ Note</span>
                          )}
                          <Edit3 className="w-3 h-3 text-[#858275]/30 group-hover:text-[#099dbb]" />
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Select value={b.status} onValueChange={v => updateBookingStatus(b.id, v)}>
                          <SelectTrigger className="h-7 text-[10px] w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">En attente</SelectItem>
                            <SelectItem value="confirmed">Confirmée</SelectItem>
                            <SelectItem value="paid">Payée</SelectItem>
                            <SelectItem value="cancelled">Annulée</SelectItem>
                            <SelectItem value="completed">Terminée</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" onClick={() => downloadPDF(b.id, 'invoice')} title="Facture"><FileText className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadPDF(b.id, 'contract')} title="Contrat"><Download className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => sendReviewRequest(b.id)} disabled={sendingReview === b.id || b.review_email_sent} title={b.review_email_sent ? "Avis déjà demandé" : "Demander un avis Google"} className={b.review_email_sent ? "text-yellow-500" : "text-yellow-600"}><Star className="w-3.5 h-3.5" fill={b.review_email_sent ? "currentColor" : "none"} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteBooking(b.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="lg:hidden divide-y divide-[#dec5ae]/20">
          {filteredBookings.map(b => {
            const ttc = (b.total_price / 100);
            const ht = (ttc / 1.20).toFixed(2);
            const tva = (ttc - parseFloat(ht)).toFixed(2);
            return (
              <div key={b.id} className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-[#0a57a4]">{b.customer?.first_name} {b.customer?.last_name}</p>
                    <p className="text-[11px] text-[#858275]">{b.customer?.email}</p>
                  </div>
                  {getStatusBadge(b.status)}
                </div>
                <div className="flex items-center gap-3 text-xs text-[#858275]">
                  <span>{formatDateFr(b.date)}</span>
                  <span>{b.time_slot}</span>
                  <span className="ml-auto font-semibold text-[#0a57a4] text-sm">{ttc.toFixed(0)}€</span>
                </div>
                {/* Notes internes */}
                <div className="pt-1">
                  {editingNotes === b.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        value={notesText}
                        onChange={e => setNotesText(e.target.value)}
                        className="border border-[#dec5ae]/50 rounded px-2 py-1 text-xs flex-1"
                        placeholder="Notes internes..."
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && saveNotes(b.id)}
                      />
                      <Button variant="ghost" size="sm" onClick={() => saveNotes(b.id)} disabled={savingNotes} className="text-green-600 h-6 w-6 p-0"><Check className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingNotes(null)} className="text-red-500 h-6 w-6 p-0"><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 cursor-pointer" onClick={() => startEditNotes(b)}>
                      {b.notes ? (
                        <span className="text-xs text-[#858275] italic">{b.notes}</span>
                      ) : (
                        <span className="text-xs text-[#858275]/40">+ Ajouter une note interne</span>
                      )}
                      <Edit3 className="w-3 h-3 text-[#858275]/30" />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-1">
                  <Select value={b.status} onValueChange={v => updateBookingStatus(b.id, v)}>
                    <SelectTrigger className="h-7 text-[10px] w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="confirmed">Confirmée</SelectItem>
                      <SelectItem value="paid">Payée</SelectItem>
                      <SelectItem value="cancelled">Annulée</SelectItem>
                      <SelectItem value="completed">Terminée</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => downloadPDF(b.id, 'invoice')} title="Facture"><FileText className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadPDF(b.id, 'contract')} title="Contrat"><Download className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => sendReviewRequest(b.id)} disabled={sendingReview === b.id || b.review_email_sent} className={b.review_email_sent ? "text-yellow-500" : "text-yellow-600"}><Star className="w-4 h-4" fill={b.review_email_sent ? "currentColor" : "none"} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteBooking(b.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PricingTab({ pricing, token, onRefresh }) {
  const [editingRates, setEditingRates] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newRate, setNewRate] = useState({ season: 'haute', duration_label: '', duration_hours: 2, time_slot: '', price: 0 });

  const startEdit = (rate) => {
    setEditingRates(prev => ({ ...prev, [rate.id]: { ...rate, priceEuros: rate.price / 100 } }));
  };

  const cancelEdit = (id) => {
    setEditingRates(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const saveRate = async (rateId) => {
    setSaving(true);
    const edited = editingRates[rateId];
    try {
      const res = await fetch(`${API}/admin/pricing/${rateId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          season: edited.season,
          duration_label: edited.duration_label,
          duration_hours: edited.duration_hours,
          time_slot: edited.time_slot,
          price: Math.round(edited.priceEuros * 100)
        })
      });
      if (!res.ok) throw new Error();
      toast.success('Tarif mis à jour');
      cancelEdit(rateId);
      onRefresh();
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const deleteRate = async (id) => {
    if (!window.confirm('Supprimer ce tarif ?')) return;
    try {
      await fetch(`${API}/admin/pricing/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Tarif supprimé');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const addRate = async () => {
    try {
      const res = await fetch(`${API}/admin/pricing`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newRate, price: Math.round(newRate.price * 100) })
      });
      if (!res.ok) throw new Error();
      toast.success('Tarif ajouté');
      setShowAdd(false);
      setNewRate({ season: 'haute', duration_label: '', duration_hours: 2, time_slot: '', price: 0 });
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const renderSeasonTable = (season, title, bgColor) => {
    const rates = pricing.filter(p => p.season === season);
    return (
      <div className="bg-white rounded-sm border border-[#dec5ae]/30">
        <div className={`p-4 border-b border-[#dec5ae]/30 flex items-center justify-between`} style={{ backgroundColor: bgColor }}>
          <h3 className="font-heading text-lg text-white">{title}</h3>
        </div>
        <div className="overflow-x-auto hidden lg:block">
          <table className="admin-table">
            <thead>
              <tr><th>Durée</th><th>Créneau</th><th>Prix TTC</th><th>Prix HT</th><th>TVA 20%</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {rates.map(rate => {
                const editing = editingRates[rate.id];
                const ttc = rate.price / 100;
                const ht = (ttc / 1.20).toFixed(2);
                const tva = (ttc - parseFloat(ht)).toFixed(2);
                if (editing) {
                  return (
                    <tr key={rate.id} className="bg-blue-50/50">
                      <td><Input value={editing.duration_label} onChange={e => setEditingRates(p => ({ ...p, [rate.id]: { ...p[rate.id], duration_label: e.target.value } }))} className="text-sm h-8 w-28" /></td>
                      <td><Input value={editing.time_slot || ''} onChange={e => setEditingRates(p => ({ ...p, [rate.id]: { ...p[rate.id], time_slot: e.target.value } }))} className="text-sm h-8 w-40" /></td>
                      <td><Input type="number" value={editing.priceEuros} onChange={e => setEditingRates(p => ({ ...p, [rate.id]: { ...p[rate.id], priceEuros: Number(e.target.value) } }))} className="text-sm h-8 w-20 font-bold" /></td>
                      <td className="text-xs text-[#858275]">{(editing.priceEuros / 1.20).toFixed(2)}€</td>
                      <td className="text-xs text-[#858275]">{(editing.priceEuros - editing.priceEuros / 1.20).toFixed(2)}€</td>
                      <td>
                        <div className="flex gap-1">
                          <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs" onClick={() => saveRate(rate.id)} disabled={saving}><Save className="w-3 h-3 mr-1" />OK</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => cancelEdit(rate.id)}>Annuler</Button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={rate.id}>
                    <td className="font-semibold">{rate.duration_label}</td>
                    <td>{rate.time_slot || '-'}</td>
                    <td className="font-semibold text-[#0a57a4]">{ttc}€</td>
                    <td className="text-xs text-[#858275]">{ht}€</td>
                    <td className="text-xs text-[#858275]">{tva}€</td>
                    <td>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(rate)} title="Modifier"><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteRate(rate.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="lg:hidden divide-y divide-[#dec5ae]/20">
          {rates.map(rate => {
            const editing = editingRates[rate.id];
            const ttc = rate.price / 100;
            const ht = (ttc / 1.20).toFixed(2);
            const tva = (ttc - parseFloat(ht)).toFixed(2);
            if (editing) {
              return (
                <div key={rate.id} className="p-3 space-y-2 bg-blue-50/50">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px]">Durée</Label><Input value={editing.duration_label} onChange={e => setEditingRates(p => ({ ...p, [rate.id]: { ...p[rate.id], duration_label: e.target.value } }))} className="text-sm h-8" /></div>
                    <div><Label className="text-[10px]">Prix TTC (€)</Label><Input type="number" value={editing.priceEuros} onChange={e => setEditingRates(p => ({ ...p, [rate.id]: { ...p[rate.id], priceEuros: Number(e.target.value) } }))} className="text-sm h-8 font-bold" /></div>
                  </div>
                  <div><Label className="text-[10px]">Créneau</Label><Input value={editing.time_slot || ''} onChange={e => setEditingRates(p => ({ ...p, [rate.id]: { ...p[rate.id], time_slot: e.target.value } }))} className="text-sm h-8" /></div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs flex-1" onClick={() => saveRate(rate.id)} disabled={saving}><Save className="w-3 h-3 mr-1" />OK</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => cancelEdit(rate.id)}>Annuler</Button>
                  </div>
                </div>
              );
            }
            return (
              <div key={rate.id} className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">{rate.duration_label}</p>
                  <p className="text-xs text-[#858275]">{rate.time_slot || '-'}</p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-semibold text-[#0a57a4]">{ttc}€</p>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(rate)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteRate(rate.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button size="sm" className="bg-[#099dbb] hover:bg-[#0a57a4] text-white" onClick={() => setShowAdd(true)} data-testid="add-rate-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un tarif
        </Button>
      </div>
      {renderSeasonTable('haute', 'Haute Saison (Juillet - Août)', '#0a57a4')}
      {renderSeasonTable('basse', 'Basse Saison (Avril-Juin, Sept-Oct)', '#099dbb')}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[#0a57a4]">Ajouter un tarif</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Saison</Label>
              <Select value={newRate.season} onValueChange={v => setNewRate(p => ({ ...p, season: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="haute">Haute Saison</SelectItem>
                  <SelectItem value="basse">Basse Saison</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Label durée</Label><Input value={newRate.duration_label} onChange={e => setNewRate(p => ({ ...p, duration_label: e.target.value }))} placeholder="Ex: Matin, 3 heures" /></div>
              <div><Label className="text-xs">Durée (h)</Label><Input type="number" value={newRate.duration_hours} onChange={e => setNewRate(p => ({ ...p, duration_hours: Number(e.target.value) }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Créneau</Label><Input value={newRate.time_slot} onChange={e => setNewRate(p => ({ ...p, time_slot: e.target.value }))} placeholder="10h00-13h00" /></div>
              <div><Label className="text-xs">Prix TTC (€)</Label><Input type="number" value={newRate.price} onChange={e => setNewRate(p => ({ ...p, price: Number(e.target.value) }))} /></div>
            </div>
            <Button onClick={addRate} className="w-full bg-[#099dbb] hover:bg-[#0a57a4] text-white">Ajouter</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BoatsTab({ boats, token, onRefresh }) {
  const [editBoat, setEditBoat] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const emptyBoat = { name: '', model: 'ROTO 450 S', capacity: 5, power: '6CV', length: '4.50m', year: 2024, features: [], images: [], description: '' };
  const [form, setForm] = useState(emptyBoat);
  const [saving, setSaving] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const [newImage, setNewImage] = useState('');
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (file, boatId) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const targetId = boatId || editBoat?.id;
      if (!targetId) {
        // For new boats, store as URL placeholder - save boat first
        toast.error('Sauvegardez le bateau d\'abord, puis ajoutez les photos');
        return;
      }
      const res = await fetch(`${API}/admin/boats/${targetId}/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Erreur upload'); }
      const data = await res.json();
      setForm(p => ({ ...p, images: data.images }));
      toast.success('Photo uploadée');
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const openEdit = (boat) => {
    setForm({ ...boat, features: boat.features || [], images: boat.images || [] });
    setEditBoat(boat);
  };

  const saveBoat = async () => {
    setSaving(true);
    try {
      const url = editBoat ? `${API}/admin/boats/${editBoat.id}` : `${API}/admin/boats`;
      const method = editBoat ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error();
      toast.success(editBoat ? 'Bateau modifié' : 'Bateau ajouté');
      setEditBoat(null);
      setShowAdd(false);
      setForm(emptyBoat);
      onRefresh();
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const deleteBoat = async (id) => {
    if (!window.confirm('Supprimer ce bateau ?')) return;
    try {
      await fetch(`${API}/admin/boats/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Bateau supprimé');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const toggleBoatActive = async (id, newStatus) => {
    try {
      await fetch(`${API}/admin/boats/${id}/toggle-active`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: newStatus })
      });
      toast.success(newStatus ? 'Bateau activé' : 'Bateau désactivé');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const addFeature = () => { if (newFeature.trim()) { setForm(p => ({ ...p, features: [...p.features, newFeature.trim()] })); setNewFeature(''); } };
  const removeFeature = (i) => setForm(p => ({ ...p, features: p.features.filter((_, idx) => idx !== i) }));
  const addImage = () => { if (newImage.trim()) { setForm(p => ({ ...p, images: [...p.images, newImage.trim()] })); setNewImage(''); } };
  const removeImage = (i) => setForm(p => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }));

  const isOpen = editBoat || showAdd;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button size="sm" className="bg-[#099dbb] hover:bg-[#0a57a4] text-white" onClick={() => { setForm(emptyBoat); setShowAdd(true); }} data-testid="add-boat-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un bateau
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {boats.map(boat => (
          <div key={boat.id} className="bg-white rounded-sm border border-[#dec5ae]/30 overflow-hidden group">
            <div className="relative">
              <img src={boat.images?.[0] || "https://images.unsplash.com/photo-1768175405386-6571a3f59382"} alt={boat.name} className="w-full h-48 object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <Button size="sm" className="bg-white text-[#0a57a4] hover:bg-[#099dbb] hover:text-white" onClick={() => openEdit(boat)}><Pencil className="w-4 h-4 mr-1" /> Modifier</Button>
                <Button size="sm" variant="outline" className="bg-white text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteBoat(boat.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div><h3 className="font-semibold text-[#0a57a4]">{BOAT_NAMES[boat.id] || boat.name}</h3><p className="text-sm text-[#858275]">{boat.model}</p></div>
                <span 
                  className={`text-xs px-2 py-1 rounded-sm cursor-pointer transition-colors ${boat.is_active ? 'bg-green-100 text-green-800 hover:bg-red-100 hover:text-red-800' : 'bg-red-100 text-red-800 hover:bg-green-100 hover:text-green-800'}`}
                  onClick={() => toggleBoatActive(boat.id, !boat.is_active)}
                  title={boat.is_active ? 'Cliquer pour désactiver' : 'Cliquer pour activer'}
                  data-testid={`toggle-boat-${boat.id}`}
                >{boat.is_active ? 'Actif' : 'Inactif'}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-sm py-3 border-y border-[#dec5ae]/30 my-3">
                <div><p className="font-semibold text-[#0a57a4]">{boat.capacity}</p><p className="text-xs text-[#858275]">Pers.</p></div>
                <div><p className="font-semibold text-[#0a57a4]">{boat.power}</p><p className="text-xs text-[#858275]">Moteur</p></div>
                <div><p className="font-semibold text-[#0a57a4]">{boat.length}</p><p className="text-xs text-[#858275]">Long.</p></div>
              </div>
              <p className="text-xs text-[#858275] line-clamp-2 mb-2">{boat.description}</p>
              <div className="flex flex-wrap gap-1">{boat.features?.map((f, i) => (<span key={i} className="text-xs bg-[#FDFBF7] px-2 py-1 rounded-sm">{f}</span>))}</div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isOpen} onOpenChange={() => { setEditBoat(null); setShowAdd(false); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-[#0a57a4]">{editBoat ? 'Modifier le bateau' : 'Ajouter un bateau'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Nom</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="boat-name" /></div>
              <div><Label className="text-xs">Modèle</Label><Input value={form.model} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label className="text-xs">Capacité</Label><Input type="number" value={form.capacity} onChange={e => setForm(p => ({ ...p, capacity: Number(e.target.value) }))} /></div>
              <div><Label className="text-xs">Moteur</Label><Input value={form.power} onChange={e => setForm(p => ({ ...p, power: e.target.value }))} /></div>
              <div><Label className="text-xs">Longueur</Label><Input value={form.length} onChange={e => setForm(p => ({ ...p, length: e.target.value }))} /></div>
              <div><Label className="text-xs">Année</Label><Input type="number" value={form.year} onChange={e => setForm(p => ({ ...p, year: Number(e.target.value) }))} /></div>
            </div>
            <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div>
              <Label className="text-xs">Photos</Label>
              <div className="flex gap-2 flex-wrap mt-1 mb-2">
                {form.images.map((img, i) => (
                  <div key={i} className="relative group/img">
                    <img src={img.startsWith('/api') ? `${API.replace('/api', '')}${img}` : img} alt="" className="w-16 h-16 object-cover rounded-sm border" />
                    <button onClick={() => removeImage(i)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center opacity-0 group-hover/img:opacity-100">x</button>
                  </div>
                ))}
              </div>
              {/* File upload */}
              <div className="mb-2">
                <label className={`flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-[#dec5ae]/50 rounded-lg cursor-pointer hover:border-[#099dbb] hover:bg-[#099dbb]/5 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <Upload className="w-4 h-4 text-[#099dbb]" />
                  <span className="text-sm text-[#858275]">{uploading ? 'Upload en cours...' : 'Télécharger une photo'}</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => { if (e.target.files[0]) uploadImage(e.target.files[0]); e.target.value = ''; }} data-testid="boat-image-upload" />
                </label>
              </div>
              {/* URL fallback */}
              <div className="flex gap-2"><Input value={newImage} onChange={e => setNewImage(e.target.value)} placeholder="Ou coller une URL d'image" className="text-sm" /><Button size="sm" variant="outline" onClick={addImage}><ImageIcon className="w-3 h-3 mr-1" />+</Button></div>
            </div>
            <div>
              <Label className="text-xs">Équipements</Label>
              <div className="flex flex-wrap gap-1 mt-1 mb-2">
                {form.features.map((f, i) => (
                  <span key={i} className="text-xs bg-[#FDFBF7] px-2 py-1 rounded-sm flex items-center gap-1">{f}<button onClick={() => removeFeature(i)} className="text-red-400 hover:text-red-600">x</button></span>
                ))}
              </div>
              <div className="flex gap-2"><Input value={newFeature} onChange={e => setNewFeature(e.target.value)} placeholder="Ex: GPS, Glacière..." className="text-sm" /><Button size="sm" variant="outline" onClick={addFeature}><Plus className="w-3 h-3" /></Button></div>
            </div>
            <Button onClick={saveBoat} disabled={saving} className="w-full bg-[#099dbb] hover:bg-[#0a57a4] text-white" data-testid="save-boat-btn">
              {saving ? 'Enregistrement...' : (editBoat ? 'Sauvegarder' : 'Créer')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MessagesTab({ messages, token, onRefresh, customers }) {
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedMsg, setExpandedMsg] = useState(null);

  const unreadCount = messages.filter(m => !m.is_read).length;

  const markRead = async (id) => {
    await fetch(`${API}/admin/messages/${id}/read`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    await fetch(`${API}/admin/messages/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    toast.success('Message supprimé');
    onRefresh();
  };

  const sendReply = async (msg) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/messages/${msg.id}/reply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply: replyText, subject: `Re: ${msg.subject || 'Votre message'} - A Bella Barca` })
      });
      if (!res.ok) throw new Error();
      toast.success(`Réponse envoyée à ${msg.email}`);
      setReplyingTo(null);
      setReplyText('');
      onRefresh();
    } catch { toast.error('Erreur envoi réponse'); }
    finally { setSending(false); }
  };

  const addToClients = async (msg) => {
    const existing = customers.find(c => c.email === msg.email);
    if (existing) { toast.info('Ce client existe déjà'); return; }
    const nameParts = msg.name.split(' ');
    const firstName = nameParts[0] || msg.name;
    const lastName = nameParts.slice(1).join(' ') || '';
    try {
      const res = await fetch(`${API}/admin/customers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: firstName, last_name: lastName, email: msg.email, phone: msg.phone || '', city: '', postal_code: '', country: 'France' })
      });
      if (!res.ok) throw new Error();
      toast.success(`${msg.name} ajouté aux clients`);
      onRefresh();
    } catch { toast.error('Erreur ajout client'); }
  };

  const toggleExpand = async (msg) => {
    if (expandedMsg === msg.id) { setExpandedMsg(null); return; }
    setExpandedMsg(msg.id);
    if (!msg.is_read) { markRead(msg.id); onRefresh(); }
  };

  return (
    <div className="space-y-4" data-testid="messages-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-xl text-[#0a57a4]">Messages</h2>
          {unreadCount > 0 && <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{unreadCount}</span>}
        </div>
        <p className="text-sm text-[#858275]">{messages.length} message(s)</p>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-12 text-[#858275]">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucun message reçu</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map(msg => (
            <div key={msg.id} className={`bg-white rounded-sm border ${msg.is_read ? 'border-[#dec5ae]/30' : 'border-[#099dbb] border-l-4'} overflow-hidden`} data-testid={`message-${msg.id}`}>
              <div className="p-3 lg:p-4 cursor-pointer" onClick={() => toggleExpand(msg)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-semibold text-sm ${msg.is_read ? 'text-[#858275]' : 'text-[#0a57a4]'}`}>{msg.name}</p>
                      {msg.subject && <span className="text-xs text-[#858275]">— {msg.subject}</span>}
                    </div>
                    <p className="text-xs text-[#858275] mt-0.5">{msg.email}{msg.phone ? ` · ${msg.phone}` : ''}</p>
                    {expandedMsg !== msg.id && <p className="text-sm text-[#858275] mt-1 truncate">{msg.message}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-[10px] text-[#858275]">{msg.created_at ? formatDateFr(msg.created_at.split('T')[0]) : ''}</span>
                  </div>
                </div>

                {expandedMsg === msg.id && (
                  <div className="mt-3">
                    <div className="bg-[#FDFBF7] p-3 rounded text-sm text-[#333] whitespace-pre-wrap border border-[#dec5ae]/20">{msg.message}</div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <Button size="sm" className="bg-[#099dbb] hover:bg-[#0a57a4] text-white text-xs" onClick={(e) => { e.stopPropagation(); setReplyingTo(msg); setReplyText(''); }} data-testid={`reply-msg-${msg.id}`}>
                        <Reply className="w-3.5 h-3.5 mr-1" /> Répondre
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={(e) => { e.stopPropagation(); addToClients(msg); }} data-testid={`add-client-${msg.id}`}>
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Ajouter aux clients
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 text-xs ml-auto" onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }} data-testid={`delete-msg-${msg.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {replyingTo?.id === msg.id && (
                <div className="border-t border-[#dec5ae]/30 p-3 lg:p-4 bg-[#FDFBF7]" onClick={(e) => e.stopPropagation()}>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={`Répondre à ${msg.name}...`}
                    className="w-full p-3 border border-[#dec5ae]/50 rounded text-sm min-h-[100px] resize-y focus:outline-none focus:border-[#099dbb]"
                    data-testid={`reply-textarea-${msg.id}`}
                  />
                  <div className="flex items-center gap-2 mt-2 justify-end">
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => { setReplyingTo(null); setReplyText(''); }}>Annuler</Button>
                    <Button size="sm" className="bg-[#099dbb] hover:bg-[#0a57a4] text-white text-xs" onClick={() => sendReply(msg)} disabled={sending || !replyText.trim()} data-testid={`send-reply-${msg.id}`}>
                      {sending ? 'Envoi...' : 'Envoyer la réponse'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function CustomersTab({ customers, token, onRefresh }) {
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showMail, setShowMail] = useState(null);
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');
  const [newCustomer, setNewCustomer] = useState({ first_name: '', last_name: '', email: '', phone: '', city: '', postal_code: '', country: 'France' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const deleteCustomer = async (customerId, customerName) => {
    if (!window.confirm(`Supprimer le client "${customerName}" et toutes ses réservations associées ?`)) return;
    setDeleting(customerId);
    try {
      const res = await fetch(`${API}/admin/customers/${customerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      toast.success('Client supprimé');
      onRefresh();
    } catch { toast.error('Erreur suppression client'); }
    finally { setDeleting(null); }
  };

  const filtered = customers.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.first_name?.toLowerCase().includes(s) || c.last_name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) || c.phone?.includes(s);
  });

  const exportCustomers = async () => {
    try {
      const res = await fetch(`${API}/admin/customers/export`, { headers: { 'Authorization': `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = 'clients_abellabarca.csv';
      a.click();
      toast.success('Base clients téléchargée');
    } catch { toast.error('Erreur export'); }
  };

  const addCustomer = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/customers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newCustomer)
      });
      if (!res.ok) throw new Error();
      toast.success('Client ajouté');
      setShowAdd(false);
      setNewCustomer({ first_name: '', last_name: '', email: '', phone: '', city: '', postal_code: '', country: 'France' });
      onRefresh();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const sendMail = () => {
    window.open(`mailto:${showMail.email}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`);
    setShowMail(null);
    setMailSubject('');
    setMailBody('');
    toast.success('Client mail ouvert');
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#858275]" />
          <Input placeholder="Rechercher un client..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="customer-search" />
        </div>
        <Button size="sm" variant="outline" onClick={exportCustomers} data-testid="export-customers-btn"><Download className="w-4 h-4 mr-1" /> Télécharger CSV</Button>
        <Button size="sm" className="bg-[#099dbb] hover:bg-[#0a57a4] text-white" onClick={() => setShowAdd(true)} data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      </div>

      <div className="text-xs text-[#858275] px-1">{filtered.length} client{filtered.length > 1 ? 's' : ''}</div>

      <div className="bg-white rounded-sm border border-[#dec5ae]/30 overflow-hidden">
        {/* Desktop table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr><th>Nom</th><th>Email</th><th>Téléphone</th><th>Ville</th><th>Inscription</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td className="font-semibold text-sm">{c.first_name} {c.last_name}</td>
                  <td className="text-sm">{c.email}</td>
                  <td className="text-sm">{c.phone}</td>
                  <td className="text-sm">{c.city || '-'}</td>
                  <td className="text-xs text-[#858275]">{c.created_at ? format(new Date(c.created_at), 'dd/MM/yyyy', { locale: fr }) : '-'}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setShowMail(c)} title="Envoyer un email" data-testid={`email-customer-${c.id}`}><Mail className="w-4 h-4 text-[#099dbb]" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteCustomer(c.id, `${c.first_name} ${c.last_name}`)} disabled={deleting === c.id} title="Supprimer le client" data-testid={`delete-customer-${c.id}`} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="lg:hidden divide-y divide-[#dec5ae]/20">
          {filtered.map(c => (
            <div key={c.id} className="p-3 space-y-1">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm text-[#0a57a4]">{c.first_name} {c.last_name}</p>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setShowMail(c)} data-testid={`email-customer-${c.id}`}><Mail className="w-4 h-4 text-[#099dbb]" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteCustomer(c.id, `${c.first_name} ${c.last_name}`)} disabled={deleting === c.id} data-testid={`delete-customer-${c.id}`} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <p className="text-xs text-[#858275]">{c.email}</p>
              <div className="flex items-center gap-3 text-xs text-[#858275]">
                <span>{c.phone}</span>
                {c.city && <span>{c.city}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Customer Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[#0a57a4]">Ajouter un client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Prénom</Label><Input value={newCustomer.first_name} onChange={e => setNewCustomer(p => ({ ...p, first_name: e.target.value }))} data-testid="new-customer-firstname" /></div>
              <div><Label className="text-xs">Nom</Label><Input value={newCustomer.last_name} onChange={e => setNewCustomer(p => ({ ...p, last_name: e.target.value }))} /></div>
            </div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={newCustomer.email} onChange={e => setNewCustomer(p => ({ ...p, email: e.target.value }))} data-testid="new-customer-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Téléphone</Label><Input value={newCustomer.phone} onChange={e => setNewCustomer(p => ({ ...p, phone: e.target.value }))} /></div>
              <div><Label className="text-xs">Ville</Label><Input value={newCustomer.city} onChange={e => setNewCustomer(p => ({ ...p, city: e.target.value }))} /></div>
            </div>
            <Button onClick={addCustomer} disabled={saving} className="w-full bg-[#099dbb] hover:bg-[#0a57a4] text-white">{saving ? 'Ajout...' : 'Ajouter le client'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Mail Dialog */}
      <Dialog open={!!showMail} onOpenChange={() => setShowMail(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[#0a57a4] flex items-center gap-2"><Mail className="w-5 h-5" /> Email à {showMail?.first_name} {showMail?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#858275]">Destinataire: {showMail?.email}</p>
            <div><Label className="text-xs">Objet</Label><Input value={mailSubject} onChange={e => setMailSubject(e.target.value)} placeholder="Objet du message" /></div>
            <div><Label className="text-xs">Message</Label><textarea value={mailBody} onChange={e => setMailBody(e.target.value)} rows={5} className="w-full border border-[#dec5ae]/40 rounded-sm p-2 text-sm resize-none" placeholder="Votre message..." /></div>
            <Button onClick={sendMail} className="w-full bg-[#099dbb] hover:bg-[#0a57a4] text-white"><Mail className="w-4 h-4 mr-2" /> Ouvrir dans ma messagerie</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
